import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    defaultHslEnhance,
    IHslEnhance,
    type ImageEnhancePlugin,
} from '@meitu/saas-editor-core/es/layers/main-layer/plugins/apps/user-editor/image-enhance';
import { useBatchActionHooks } from '../batch-action-hooks';
import EnhanceSlider from '../enhance-slider';
import Reset from '../reset';
import SelectColor from '../select-color';
import { IEnhanceFeatureProps, IEnhanceSlider } from '../types';
import { getSliderConfig } from './config';

export const getHslArgs = (data: IHslEnhance) => {
    const index = data.selectIndex;

    return {
        hslPickColors: [data.hsl.hsl_hue[index], data.hsl.hsl_saturation[index], data.hsl.hsl_light[index]],
        pickColors: data.hsl.pickColors ? data.hsl.pickColors?.split(',').map((v) => +v / 255) : '',
    };
};

export const HSLFeature: React.FC<IEnhanceFeatureProps> = ({
    editor,
    theme,
    onReset,
    onBeforeEnhance,
    onEnhance,
    onAfterEnhance,
    onSelect,
    trackFunc,
    isBatchEditor,
    setStageLoading,
    batchSetIsLoading,
    setIsDisable,
}) => {
    const { batchAction } = useBatchActionHooks();
    const { basicEnhance } = editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin');
    const [enhanceState, _setEnhanceState] = useState(
        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').getState(),
    );

    useEffect(() => {
        return editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').watch((state) => _setEnhanceState(state));
    }, []);

    const { hslEnhance, extractColors } = enhanceState;

    const skinList = useRef<Array<IEnhanceSlider[]>>();
    const [sliderSkin, setSliderSkin] = useState<IEnhanceSlider[]>([]);

    useEffect(() => {
        skinList.current = getSliderConfig(theme);
        setSliderSkin(skinList.current[hslEnhance.selectIndex]);
    }, [theme, hslEnhance.selectIndex]);

    const colorList = useMemo(() => {
        return extractColors.map((v) => {
            return {
                key: v,
                value: v,
            };
        });
    }, [extractColors]);

    useEffect(() => {
        if (!colorList.length) return;
        const newData = cloneDeep(hslEnhance);

        newData.hsl.pickColors = colorList[hslEnhance.selectIndex].value.replace(/rgba\(|\)/g, '');

        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setHslEnhance(newData);
        // TODO: pickColors 类型问题
        basicEnhance.action(getHslArgs(newData) as any, trackFunc);
    }, [colorList, hslEnhance]);

    return (
        <>
            <div>
                <SelectColor
                    colorList={colorList}
                    index={hslEnhance.selectIndex}
                    onSelectIndex={(index) => {
                        editor
                            .getInstance<ImageEnhancePlugin>('ImageEnhancePlugin')
                            .setHslEnhance({ ...hslEnhance, selectIndex: index });
                        onSelect?.(index);
                    }}
                />
                {sliderSkin.map((v, i) => {
                    const key = v.featureType as 'hsl';
                    const keyName = v.keyName as 'hsl_hue' | 'hsl_saturation' | 'hsl_light';

                    return (
                        <EnhanceSlider
                            key={i}
                            maxVal={v.maxVal}
                            minVal={v.minVal}
                            value={hslEnhance[key][keyName][hslEnhance.selectIndex]}
                            title={v.name}
                            opacityOpposite={v.opacityOpposite}
                            onChange={async (val: number) => {
                                const newParams = cloneDeep(hslEnhance);

                                newParams[key][keyName][hslEnhance.selectIndex] = val;

                                editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setHslEnhance(newParams);
                                onBeforeEnhance?.(v.keyName, val);
                                // TODO: pickColors 类型问题
                                await editor
                                    .getInstance<ImageEnhancePlugin>('ImageEnhancePlugin')
                                    .basicEnhance.action(getHslArgs(newParams) as any, trackFunc);
                                onEnhance?.(v.keyName, val);
                            }}
                            onAfterChange={async (val) => {
                                if (isBatchEditor) {
                                    const newParams = cloneDeep(hslEnhance);

                                    newParams[v.featureType][v.keyName] = val;
                                    batchAction(
                                        editor,
                                        getHslArgs(newParams) as any,
                                        (stageIndex: number, isBatch: boolean) => {
                                            setStageLoading?.(stageIndex, isBatch);
                                        },
                                        setIsDisable,
                                        batchSetIsLoading,
                                    );
                                }
                                onAfterEnhance?.(v.keyName, val);
                            }}
                            background={v.background}
                            wrapBackground={v.wrapBackground}
                        />
                    );
                })}
            </div>
            <Reset
                onClick={async () => {
                    const newParams = cloneDeep(defaultHslEnhance);

                    editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setHslEnhance(newParams);
                    if (isBatchEditor) {
                        setIsDisable?.(true);
                        await editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').basicEnhance.batchReset();
                        setIsDisable?.(false);
                    } else {
                        // TODO: pickColors 类型问题
                        basicEnhance.action(getHslArgs(newParams) as any);
                    }
                    // TODO: pickColors 类型问题

                    onReset?.();
                }}
            />
        </>
    );
};
